import { parseProblem } from '@reos/api-client';
import type { ApiPaths } from '@reos/api-client';
import { apiClient, baseUrl } from './api.js';
import { DomainApiError, unwrap } from './api-error.js';

/**
 * Krok C3: klient nad `/v1/admin/orders/*` (@reos/commerce). Rucni typy,
 * ne generovany `apiClient` - `docs/api/openapi.json` je v tuto chvili
 * zastaraly (poradi cest driftovalo pres mnoho predchozich kroku, ktere ho
 * neregenerovaly; plna regenerace by do tohoto kroku vnesla desetitisice
 * nesouvisejicich radku, viz REPORT-step-C3.md, sekce Nalezy). Tvar
 * odpovida `orderSchema`/`adminOrderQueueResponseSchema` v api-schemas.ts.
 */

/**
 * Krok WEBF2: jedna polozka objednavky. `fulfillment = 'MANUAL'` a
 * `deliveredAt = null` znamena OTEVRENY UKOL - prave kvuli nemu objednavka
 * ve fronte visi.
 */
export interface OrderQueueItemLine {
  readonly productCode: string;
  readonly nameKey: string;
  readonly kind: 'MAIN' | 'ADDON';
  readonly amountGross: string;
  readonly currency: string;
  readonly fulfillment: 'AUTOMATIC' | 'MANUAL';
  readonly deliveredAt: string | null;
}

export interface OrderQueueItem {
  readonly id: string;
  readonly orderNumber: string;
  readonly productCode: string;
  readonly status: string;
  readonly addressLabel: string;
  readonly email: string;
  readonly createdAt: string;
  readonly needsReviewReason: string | null;
  readonly attentionReason: string | null;
  readonly deliverySlaBreached: boolean;
  /**
   * Krok REV1: kolikaty beh proverení objednavka ma (1 = zalozeni). Starsi
   * atrapa odpovedi ho nemusi mit, proto `?? 1` na strane obrazovky.
   */
  readonly runAttempts?: number | undefined;
  /** Krok WEBF2: prazdne u objednavek zalozenych pred WEBF2. */
  readonly items?: readonly OrderQueueItemLine[] | undefined;
}

async function unwrapFetch<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const problem = await parseProblem(res).catch(() => null);
    throw new DomainApiError(problem?.detail ?? problem?.title ?? `HTTP ${res.status}`, res.status, problem?.type);
  }
  return (await res.json()) as T;
}

export async function listOrderQueue(): Promise<OrderQueueItem[]> {
  const res = await fetch(`${baseUrl}/v1/admin/orders/queue`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  const body = await unwrapFetch<{ items: OrderQueueItem[] }>(res);
  return body.items;
}

export async function forceDeliverOrder(orderId: string, reason: string): Promise<OrderQueueItem> {
  const res = await fetch(`${baseUrl}/v1/admin/orders/${encodeURIComponent(orderId)}/force-deliver`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return unwrapFetch<OrderQueueItem>(res);
}

/**
 * Krok REV1: NOVY BEH PROVERENI po spadlem behu. `created: false` znamena,
 * ze beh uz ve fronte cekal nebo bezel - nic se nezaradilo a fronta to musi
 * rict, jinak clovek kliká dal.
 */
export interface RerunResult {
  readonly order: OrderQueueItem;
  readonly created: boolean;
  readonly attempt: number;
}

export async function rerunOrderRun(orderId: string, reason: string): Promise<RerunResult> {
  const res = await fetch(`${baseUrl}/v1/admin/orders/${encodeURIComponent(orderId)}/rerun`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return unwrapFetch<RerunResult>(res);
}

export async function refundOrder(orderId: string, reason: string): Promise<OrderQueueItem> {
  const res = await fetch(`${baseUrl}/v1/admin/orders/${encodeURIComponent(orderId)}/refund`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return unwrapFetch<OrderQueueItem>(res);
}

/**
 * Krok WEBF2: „doplnek dodan" - zavira RUCNI ukol u konkretni polozky
 * objednavky (dalsi list vlastnictvi, dalsi prověřená osoba). Kod produktu
 * jde v TELE, ne v ceste (viz `addonDeliveredBodySchema` v @reos/commerce).
 */
export async function markAddonDelivered(
  orderId: string,
  productCode: string,
): Promise<OrderQueueItem> {
  const res = await fetch(
    `${baseUrl}/v1/admin/orders/${encodeURIComponent(orderId)}/addon-delivered`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ productCode }),
    },
  );
  return unwrapFetch<OrderQueueItem>(res);
}

/**
 * Krok NAP11 (A25) — OBJEDNAVKA REPORTU Z ADMINU.
 *
 * Cesty uz existovaly a nikdo z adminu je nevolal:
 *  - `GET /v1/public/catalog`  → cenik (produkty, doplnky, rozsah s `surcharges`),
 *  - `GET /v1/public/address-lookup` → adresni misto RUIAN (`ruianAddressCode`),
 *  - `POST /v1/orders` (`createTenantOrderBodySchema`) → objednavka tenanta.
 *
 * Tvar odpovedi katalogu je `catalogItemSchema` v @reos/commerce; tady je
 * jen podmnozina, kterou formular potrebuje (stejny duvod rucnich typu jako
 * u fronty objednavek vys).
 */
export interface CatalogItem {
  readonly code: string;
  readonly nameKey: string;
  readonly priceGross: string;
  /**
   * Krok CEN2: cena bez DPH a fakturacni obdobi radku — Nastaveni -> Tarif
   * z nich kresli prepinac mesicne/rocne (rocni tarif je samostatny radek
   * ceniku, `YEARLY`), takze zadna cena nevznika prepoctem v adminu.
   */
  readonly priceNet?: string | undefined;
  readonly billingPeriod?: 'ONE_OFF' | 'MONTHLY' | 'YEARLY' | null | undefined;
  readonly planPriceGross: string | null;
  readonly currency: string;
  readonly kind: 'REPORT' | 'PACKAGE' | 'ACCESS' | 'PLAN' | 'ADDON' | string;
  readonly purchasable: boolean;
  readonly includedInPlanCodes?: readonly string[] | undefined;
  readonly scope?: { readonly surcharges: readonly string[] } | null | undefined;
}

export async function listCatalog(): Promise<readonly CatalogItem[]> {
  const res = await fetch(`${baseUrl}/v1/public/catalog`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  const body = await unwrapFetch<{ items: CatalogItem[] }>(res);
  return body.items;
}

/** Jeden navrh adresniho registru (`addressLookupItemSchema`). */
export interface OrderAddressHit {
  readonly formatted: string;
  readonly ruianAddressCode: string | null;
  readonly approximate: boolean;
}

/**
 * Napoveda adresy PRO OBJEDNAVKU. Narocne jen na jedno: objednavka reportu
 * potrebuje `ruianAddressCode` (bez nej server cil neoveri a odmitne ji),
 * takze se tu — na rozdil od `lib/address-suggest.ts`, ktere resi POLOHU
 * nemovitosti v pruvodci — zadna zaloha z Mapy.com nepouziva: navrh bez kodu
 * adresniho mista by objednat nesel.
 */
export async function lookupOrderAddress(query: string): Promise<readonly OrderAddressHit[]> {
  const res = await fetch(
    `${baseUrl}/v1/public/address-lookup?q=${encodeURIComponent(query.trim())}`,
    { credentials: 'include', headers: { accept: 'application/json' } },
  );
  const body = await unwrapFetch<{ items: OrderAddressHit[] }>(res);
  return body.items.filter((item) => item.ruianAddressCode !== null && !item.approximate);
}

export interface CreateReportOrderInput {
  readonly productCode: string;
  readonly ruianAddressCode: string;
  readonly email: string;
  readonly billingName: string;
  /** Kody doplnku z katalogu; cenu sectou server (CLAUDE.md: klient cenu neposila). */
  readonly addons: readonly string[];
  /** true = uhradit z kreditu tarifu (bez volneho kreditu konci 409). */
  readonly useCredit: boolean;
}

export interface CreatedOrder {
  readonly orderNumber: string;
  readonly status: string;
  readonly publicToken: string;
  /**
   * Krok NAP12 (A28): v DEMO kancelari se objednavka PREHRALA z fixtury a nic
   * se nezaplatilo — server posila hotovy text stitku „Ukázkový výsledek…".
   * Mimo demo pole v odpovedi vubec neni.
   */
  readonly demoSampleLabel?: string | null | undefined;
}

export async function createReportOrder(input: CreateReportOrderInput): Promise<CreatedOrder> {
  const res = await fetch(`${baseUrl}/v1/orders`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      productCode: input.productCode,
      target: { ruianAddressCode: input.ruianAddressCode },
      email: input.email,
      billing: { name: input.billingName, country: 'CZ' },
      paymentMethod: 'PROFORMA',
      useCredit: input.useCredit,
      addons: input.addons,
    }),
  });
  return unwrapFetch<CreatedOrder>(res);
}

/**
 * Krok D7 bod 3: naroky tenanta z `GET /v1/entitlements` (@reos/commerce).
 * Typ je generovany z `docs/api/openapi.json` — tahle cesta uz je po D7
 * aktualni (kind zna `MEMBERSHIP`), takze se nic neopisuje rucne.
 */
export type EntitlementItem =
  ApiPaths['/v1/entitlements']['get']['responses']['200']['content']['application/json']['items'][number];

export async function listEntitlements(): Promise<EntitlementItem[]> {
  const body = await unwrap(apiClient.GET('/v1/entitlements', {}));
  return body.items;
}
